import React, { useEffect, useMemo, useState } from 'react';
import './DashboardSchoolCalendar.css';
import '../../pages/Calendar/Calendar.css';
import { supabase } from '../../lib/supabase';
import {
  getAttachmentPublicUrl,
  isValidUrl,
  openExternalUrl,
  toAbsoluteUrl,
  toISODate,
  updateAssignment,
  deleteAssignment,
  uploadAssignmentAttachment,
} from '../../lib/calendarAssignments';
import { createVacation, deleteVacation, fetchVacationsInRange, getVacationReasonForISO, toISODateOnly, updateVacation } from '../../lib/schoolVacations';

const getTeacherName = (row) => {
  const u = row?.created_by_user || row?.users || row;
  if (!u) return 'Teacher';
  return u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'Teacher';
};

const DashboardSchoolCalendar = ({
  schoolId,
  grades = [],
  gradeSections = [],
  teachers = [],
  readOnly = false,
}) => {
  const todayISO = useMemo(() => toISODate(new Date()), []);
  const [rangeStart, setRangeStart] = useState(() => {
    const d = new Date();
    return toISODate(new Date(d.getFullYear(), d.getMonth(), 1));
  });
  const [rangeEnd, setRangeEnd] = useState(() => {
    const d = new Date();
    return toISODate(new Date(d.getFullYear(), d.getMonth() + 1, 0));
  });

  const [gradeFilter, setGradeFilter] = useState('all');
  const [teacherFilter, setTeacherFilter] = useState('all');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [assignments, setAssignments] = useState([]);
  const [vacations, setVacations] = useState([]);

  // Calendar view state
  const [calendarView, setCalendarView] = useState('week'); // 'week' or 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarAssignments, setCalendarAssignments] = useState([]);
  const [calendarVacations, setCalendarVacations] = useState([]);

  // Assignment edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [removeExistingAttachment, setRemoveExistingAttachment] = useState(false);
  const [form, setForm] = useState({
    assigned_date: '',
    title: '',
    description: '',
    link_url: '',
    grade_id: '',
    section_id: '',
  });

  // Vacation form
  const [vacForm, setVacForm] = useState({
    title: '',
    start_date: '',
    end_date: '',
  });
  const [vacSaving, setVacSaving] = useState(false);

  // Vacation edit modal
  const [showVacEditModal, setShowVacEditModal] = useState(false);
  const [editingVacation, setEditingVacation] = useState(null);
  const [vacEditSaving, setVacEditSaving] = useState(false);
  const [vacEditForm, setVacEditForm] = useState({
    title: '',
    start_date: '',
    end_date: '',
  });

  // Confirmation dialogs
  const [showDeleteAssignmentConfirm, setShowDeleteAssignmentConfirm] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  const [showDeleteVacationConfirm, setShowDeleteVacationConfirm] = useState(false);
  const [vacationToDelete, setVacationToDelete] = useState(null);

  const gradeById = useMemo(() => {
    const map = new Map();
    (grades || []).forEach(g => map.set(g.id, g));
    return map;
  }, [grades]);

  const sectionsByGradeId = useMemo(() => {
    const map = new Map();
    (gradeSections || []).forEach(s => {
      if (!map.has(s.grade_id)) map.set(s.grade_id, []);
      map.get(s.grade_id).push(s);
    });
    // Keep consistent ordering
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
    }
    return map;
  }, [gradeSections]);

  const teacherById = useMemo(() => {
    const map = new Map();
    (teachers || []).forEach(t => map.set(t.id, t));
    return map;
  }, [teachers]);

  const loadAll = async () => {
    if (!schoolId) return;
    setLoading(true);
    setError(null);
    try {
      // Assignments
      let q = supabase
        .from('calendar_assignments')
        .select(`
          id,
          school_id,
          grade_id,
          section_id,
          assigned_date,
          title,
          description,
          link_url,
          attachment_path,
          attachment_name,
          attachment_type,
          created_by,
          created_at,
          updated_at,
          created_by_user:users!calendar_assignments_created_by_fkey(
            id,
            full_name,
            first_name,
            last_name
          )
        `)
        .eq('school_id', schoolId)
        .gte('assigned_date', rangeStart)
        .lte('assigned_date', rangeEnd)
        .order('assigned_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (teacherFilter !== 'all') q = q.eq('created_by', teacherFilter);
      if (gradeFilter !== 'all' && String(gradeFilter).includes(':')) {
        const [gradeId, sectionId] = String(gradeFilter).split(':');
        // If filtering to a specific section, show:
        // 1) grade-wide (section_id is null)
        // 2) section-specific (section_id matches)
        q = q.eq('grade_id', gradeId).or(`section_id.is.null,section_id.eq.${sectionId}`);
      } else if (gradeFilter !== 'all') {
        q = q.eq('grade_id', gradeFilter);
      }

      const [{ data: aData, error: aErr }, vData] = await Promise.all([
        q,
        fetchVacationsInRange({ schoolId, startDateISO: rangeStart, endDateISO: rangeEnd }),
      ]);

      if (aErr) throw aErr;
      setAssignments(aData || []);
      setVacations(vData || []);
    } catch (e) {
      setError(e.message || 'Failed to load calendar data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, rangeStart, rangeEnd, gradeFilter, teacherFilter]);

  // Realtime subscription for vacations
  useEffect(() => {
    if (!schoolId) return;

    const channel = supabase
      .channel('school_vacations_changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'school_vacations',
          filter: `school_id=eq.${schoolId}`,
        },
        async (payload) => {
          // Reload vacations when any change occurs
          try {
            const vData = await fetchVacationsInRange({ 
              schoolId, 
              startDateISO: rangeStart, 
              endDateISO: rangeEnd 
            });
            setVacations(vData || []);
            
            // Also update calendar vacations - calculate month range from currentDate
            const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
            const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
            const monthVData = await fetchVacationsInRange({ 
              schoolId, 
              startDateISO: toISODate(monthStart), 
              endDateISO: toISODate(monthEnd) 
            });
            setCalendarVacations(monthVData || []);
          } catch (e) {
            }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, rangeStart, rangeEnd, currentDate]);

  const openEdit = (row) => {
    setError(null);
    setAttachmentFile(null);
    setRemoveExistingAttachment(false);
    setEditing(row);
    setForm({
      assigned_date: row?.assigned_date || todayISO,
      title: row?.title || '',
      description: row?.description || '',
      link_url: row?.link_url || '',
      grade_id: row?.grade_id || '',
      section_id: row?.section_id || '',
    });
    setShowEditModal(true);
  };

  const closeEdit = () => {
    setShowEditModal(false);
    setEditing(null);
    setSaving(false);
    setAttachmentFile(null);
    setRemoveExistingAttachment(false);
  };

  const saveEdit = async () => {
    if (!editing?.id) return;
    try {
      if (!form.assigned_date) throw new Error('Date is required.');
      if (!form.title?.trim()) throw new Error('Title is required.');
      if (!form.grade_id) throw new Error('Grade is required.');
      if (!isValidUrl(form.link_url)) throw new Error('Please enter a valid link URL.');

      setSaving(true);
      let attachment = null;
      if (attachmentFile) {
        attachment = await uploadAssignmentAttachment({
          file: attachmentFile,
          schoolId,
          bucket: 'assignments',
        });
      }

      const updates = {
        assigned_date: form.assigned_date,
        title: form.title.trim(),
        description: form.description || null,
        link_url: toAbsoluteUrl(form.link_url) || null,
        grade_id: form.grade_id,
        section_id: form.section_id || null,
      };

      if (attachment) {
        updates.attachment_path = attachment.path;
        updates.attachment_name = attachment.name;
        updates.attachment_type = attachment.type;
      } else if (removeExistingAttachment) {
        updates.attachment_path = null;
        updates.attachment_name = null;
        updates.attachment_type = null;
      }

      await updateAssignment({ assignmentId: editing.id, updates });
      closeEdit();
      await loadAll();
      
      // Also refresh calendar assignments for the current month
      const q = supabase
        .from('calendar_assignments')
        .select(`
          id,
          school_id,
          grade_id,
          section_id,
          assigned_date,
          title,
          description,
          link_url,
          attachment_path,
          attachment_name,
          attachment_type,
          created_by,
          created_at,
          updated_at,
          created_by_user:users!calendar_assignments_created_by_fkey(
            id,
            full_name,
            first_name,
            last_name
          )
        `)
        .eq('school_id', schoolId)
        .gte('assigned_date', monthRange.startISO)
        .lte('assigned_date', monthRange.endISO)
        .order('assigned_date', { ascending: true });
      
      const { data: aData } = await q;
      setCalendarAssignments(aData || []);
    } catch (e) {
      setError(e.message || 'Failed to update assignment.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAssignment = (row) => {
    if (!row?.id) return;
    setAssignmentToDelete(row);
    setShowDeleteAssignmentConfirm(true);
  };

  const confirmDeleteAssignment = async () => {
    if (!assignmentToDelete?.id) return;
    try {
      await deleteAssignment({ assignmentId: assignmentToDelete.id });
      await loadAll();
      // Also refresh calendar assignments for the current month
      const q = supabase
        .from('calendar_assignments')
        .select(`
          id,
          school_id,
          grade_id,
          section_id,
          assigned_date,
          title,
          description,
          link_url,
          attachment_path,
          attachment_name,
          attachment_type,
          created_by,
          created_at,
          updated_at,
          created_by_user:users!calendar_assignments_created_by_fkey(
            id,
            full_name,
            first_name,
            last_name
          )
        `)
        .eq('school_id', schoolId)
        .gte('assigned_date', monthRange.startISO)
        .lte('assigned_date', monthRange.endISO)
        .order('assigned_date', { ascending: true });
      
      const { data: aData } = await q;
      setCalendarAssignments(aData || []);
      setShowDeleteAssignmentConfirm(false);
      setAssignmentToDelete(null);
    } catch (e) {
      setError(e.message || 'Failed to delete assignment.');
      setShowDeleteAssignmentConfirm(false);
      setAssignmentToDelete(null);
    }
  };

  const cancelDeleteAssignment = () => {
    setShowDeleteAssignmentConfirm(false);
    setAssignmentToDelete(null);
  };

  const addVacation = async () => {
    try {
      if (readOnly) return;
      if (!vacForm.start_date) throw new Error('Vacation start date is required.');
      if (!vacForm.end_date) throw new Error('Vacation end date is required.');

      setVacSaving(true);
      const newVacation = await createVacation({
        schoolId,
        title: vacForm.title,
        startDateISO: vacForm.start_date,
        endDateISO: vacForm.end_date,
      });
      setVacForm({ title: '', start_date: '', end_date: '' });
      
      // Update vacations list immediately
      await loadAll();
      
      // Update calendar vacations immediately if the new vacation overlaps with current month
      const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      const monthStartISO = toISODate(monthStart);
      const monthEndISO = toISODate(monthEnd);
      
      // Check if new vacation overlaps with current month
      const overlaps = newVacation.start_date <= monthEndISO && newVacation.end_date >= monthStartISO;
      
      if (overlaps) {
        // Refresh calendar vacations to include the new one
        const monthVData = await fetchVacationsInRange({ 
          schoolId, 
          startDateISO: monthStartISO, 
          endDateISO: monthEndISO 
        });
        setCalendarVacations(monthVData || []);
      }
    } catch (e) {
      setError(e.message || 'Failed to create vacation.');
    } finally {
      setVacSaving(false);
    }
  };

  const removeVacation = (v) => {
    if (!v?.id) return;
    setVacationToDelete(v);
    setShowDeleteVacationConfirm(true);
  };

  const confirmDeleteVacation = async () => {
    if (!vacationToDelete?.id) return;
    try {
      await deleteVacation({ vacationId: vacationToDelete.id });
      
      // Update vacations list immediately
      await loadAll();
      
      // Update calendar vacations immediately
      const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      const monthVData = await fetchVacationsInRange({ 
        schoolId, 
        startDateISO: toISODate(monthStart), 
        endDateISO: toISODate(monthEnd) 
      });
      setCalendarVacations(monthVData || []);
      
      setShowDeleteVacationConfirm(false);
      setVacationToDelete(null);
    } catch (e) {
      setError(e.message || 'Failed to remove vacation.');
      setShowDeleteVacationConfirm(false);
      setVacationToDelete(null);
    }
  };

  const cancelDeleteVacation = () => {
    setShowDeleteVacationConfirm(false);
    setVacationToDelete(null);
  };

  const openVacationEdit = (v) => {
    if (!v?.id) return;
    setError(null);
    setEditingVacation(v);
    setVacEditForm({
      title: v.title || '',
      start_date: v.start_date || '',
      end_date: v.end_date || '',
    });
    setShowVacEditModal(true);
  };

  const closeVacationEdit = () => {
    setShowVacEditModal(false);
    setEditingVacation(null);
    setVacEditSaving(false);
    setVacEditForm({ title: '', start_date: '', end_date: '' });
  };

  const saveVacationEdit = async () => {
    if (!editingVacation?.id) return;
    try {
      if (readOnly) return;
      if (!vacEditForm.start_date) throw new Error('Vacation start date is required.');
      if (!vacEditForm.end_date) throw new Error('Vacation end date is required.');
      setVacEditSaving(true);
      await updateVacation({
        vacationId: editingVacation.id,
        updates: {
          title: vacEditForm.title?.trim() || null,
          start_date: vacEditForm.start_date,
          end_date: vacEditForm.end_date,
        },
      });
      closeVacationEdit();
      
      // Update vacations list immediately
      await loadAll();
      
      // Update calendar vacations immediately
      const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      const monthVData = await fetchVacationsInRange({ 
        schoolId, 
        startDateISO: toISODate(monthStart), 
        endDateISO: toISODate(monthEnd) 
      });
      setCalendarVacations(monthVData || []);
    } catch (e) {
      setError(e.message || 'Failed to update vacation.');
    } finally {
      setVacEditSaving(false);
    }
  };

  const formatGradeLabel = (a) => {
    const grade = gradeById.get(a.grade_id);
    const section = a.section_id ? gradeSections.find(s => s.id === a.section_id) : null;
    if (section) return `${grade?.name || 'Grade'} ${section.name}`;
    return grade?.name || 'Grade';
  };

  // Calendar helper functions
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Get days of the current week (Sunday to Saturday)
  const getWeekDays = (date) => {
    const currentDay = date.getDay(); // 0 = Sunday, 6 = Saturday
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - currentDay); // Go to Sunday

    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      days.push(day);
    }
    return days;
  };

  // Get days in month
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  // Check if date is vacation
  const isVacationISO = useMemo(() => {
    const ranges = (calendarVacations || [])
      .map(v => ({ start: toISODateOnly(v.start_date), end: toISODateOnly(v.end_date) }))
      .filter(v => v.start && v.end);
    return (iso) => {
      if (!iso) return false;
      const isoDay = toISODateOnly(iso);
      return ranges.some(r => isoDay >= r.start && isoDay <= r.end);
    };
  }, [calendarVacations]);

  // Calendar month range (for loading assignments)
  const monthRange = useMemo(() => {
    const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    return { startISO: toISODate(start), endISO: toISODate(end) };
  }, [currentDate]);

  // Assignments by date for calendar view
  const assignmentsByDate = useMemo(() => {
    const map = new Map();
    (calendarAssignments || []).forEach(a => {
      const key = a.assigned_date; // YYYY-MM-DD
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    });
    return map;
  }, [calendarAssignments]);

  // Selected date assignments
  const selectedDateAssignments = useMemo(() => {
    if (!selectedDate) return [];
    const key = toISODate(selectedDate);
    return assignmentsByDate.get(key) || [];
  }, [selectedDate, assignmentsByDate]);

  // Header label
  const headerLabel = useMemo(() => {
    if (calendarView === 'month') {
      return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    }

    // Week View range
    const weekDays = getWeekDays(selectedDate || currentDate);
    const start = weekDays[0];
    const end = weekDays[6];

    const startDay = start.getDate();
    const endDay = end.getDate();
    const startMonth = monthNames[start.getMonth()].substring(0, 3);
    const endMonth = monthNames[end.getMonth()].substring(0, 3);
    const startYear = start.getFullYear();
    const endYear = end.getFullYear();

    if (startYear !== endYear) {
      return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`;
    }
    if (start.getMonth() !== end.getMonth()) {
      return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${startYear}`;
    }
    return `${startDay} - ${endDay} ${startMonth} ${startYear}`;
  }, [calendarView, currentDate, selectedDate]);

  // Check if date is today
  const isToday = (day) => {
    if (!day) return false;
    const today = new Date();
    const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return dateToCheck.toDateString() === today.toDateString();
  };

  // Check if date is selected
  const isSelected = (day) => {
    if (!day || !selectedDate) return false;
    const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return dateToCheck.toDateString() === selectedDate.toDateString();
  };

  // Navigate calendar
  const goToPrevious = () => {
    if (calendarView === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() - 7);
      setCurrentDate(newDate);
      if (selectedDate) {
        const nextSelected = new Date(selectedDate);
        nextSelected.setDate(selectedDate.getDate() - 7);
        setSelectedDate(nextSelected);
      }
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
      setSelectedDate(null);
    }
  };

  const goToNext = () => {
    if (calendarView === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() + 7);
      setCurrentDate(newDate);
      if (selectedDate) {
        const nextSelected = new Date(selectedDate);
        nextSelected.setDate(selectedDate.getDate() + 7);
        setSelectedDate(nextSelected);
      }
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
      setSelectedDate(null);
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  // Handle date click
  const handleDateClick = (dayOrDate) => {
    if (dayOrDate) {
      const clickedDate = dayOrDate instanceof Date
        ? new Date(dayOrDate.getFullYear(), dayOrDate.getMonth(), dayOrDate.getDate())
        : new Date(currentDate.getFullYear(), currentDate.getMonth(), dayOrDate);
      setSelectedDate(clickedDate);
      setCurrentDate(clickedDate);
    }
  };

  // Load calendar assignments (for the current month being viewed)
  useEffect(() => {
    let cancelled = false;

    async function loadCalendarData() {
      if (!schoolId) return;

      try {
        // Load assignments for the calendar month
        let q = supabase
          .from('calendar_assignments')
          .select(`
            id,
            school_id,
            grade_id,
            section_id,
            assigned_date,
            title,
            description,
            link_url,
            attachment_path,
            attachment_name,
            attachment_type,
            created_by,
            created_at,
            updated_at,
            created_by_user:users!calendar_assignments_created_by_fkey(
              id,
              full_name,
              first_name,
              last_name
            )
          `)
          .eq('school_id', schoolId)
          .gte('assigned_date', monthRange.startISO)
          .lte('assigned_date', monthRange.endISO)
          .order('assigned_date', { ascending: true });

        const [{ data: aData, error: aErr }, vData] = await Promise.all([
          q,
          fetchVacationsInRange({ schoolId, startDateISO: monthRange.startISO, endDateISO: monthRange.endISO }),
        ]);

        if (cancelled) return;

        if (aErr) throw aErr;
        setCalendarAssignments(aData || []);
        setCalendarVacations(vData || []);
      } catch (e) {
        }
    }

    loadCalendarData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, monthRange]);

  const days = getDaysInMonth(currentDate);

  return (
    <div className="mds-school-cal">
      {/* Calendar View Section */}
      <div className="mds-school-cal__section">
        <div className="mds-school-cal__header">
          <div>
            <h3 className="mds-school-cal__title">Calendar View</h3>
            <p className="mds-school-cal__sub">
              Visual calendar showing all assignments and vacation days.
            </p>
          </div>
        </div>

        {/* Calendar Controls */}
        <div className="calendar-header" style={{ marginBottom: '1rem' }}>
          <div className="calendar-controls">
            <button className="calendar-btn today-btn" onClick={goToToday}>
              Today
            </button>
            <div className="calendar-navigation">
              <button className="calendar-btn nav-btn" onClick={goToPrevious}>
                ‹
              </button>
              <h2 className="calendar-month-year">
                {headerLabel}
              </h2>
              <button className="calendar-btn nav-btn" onClick={goToNext}>
                ›
              </button>
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="calendar-container">
          <div className="calendar-view-header">
            <button
              className="calendar-view-toggle"
              onClick={() => setCalendarView(calendarView === 'week' ? 'month' : 'week')}
              title={calendarView === 'week' ? 'Expand to month view' : 'Collapse to week view'}
            >
              {calendarView === 'week' ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <polyline points="9 21 3 21 3 15"></polyline>
                  <line x1="21" y1="3" x2="14" y2="10"></line>
                  <line x1="3" y1="21" x2="10" y2="14"></line>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="4 14 10 14 10 20"></polyline>
                  <polyline points="20 10 14 10 14 4"></polyline>
                  <line x1="14" y1="10" x2="21" y2="3"></line>
                  <line x1="3" y1="21" x2="10" y2="14"></line>
                </svg>
              )}
            </button>
          </div>
          <div className="calendar-grid">
            {/* Day headers */}
            <div className="calendar-day-headers">
              {dayNames.map(day => (
                <div key={day} className="calendar-day-header">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days - Week View */}
            {calendarView === 'week' && (
              <div className="calendar-days calendar-week-view">
                {getWeekDays(selectedDate || currentDate).map((dateObj, index) => {
                  const day = dateObj.getDate();
                  const key = toISODate(dateObj);
                  const dayAssignments = assignmentsByDate.get(key) || [];
                  const isTodayDate = dateObj.toDateString() === new Date().toDateString();
                  const isSelectedDate = selectedDate && dateObj.toDateString() === selectedDate.toDateString();
                  const isCurrentMonth = dateObj.getMonth() === currentDate.getMonth();
                  const isVacation = isVacationISO(key);
                  return (
                    <div
                      key={index}
                      className={`calendar-day week-day ${isTodayDate ? 'today' : ''} ${isSelectedDate ? 'selected' : ''} ${!isCurrentMonth ? 'other-month' : ''} ${isVacation ? 'vacation' : ''}`}
                      onClick={() => {
                        setCurrentDate(dateObj);
                        handleDateClick(dateObj);
                      }}
                      title={isVacation ? (getVacationReasonForISO(calendarVacations, key) || 'Vacation / day off') : undefined}
                    >
                      <span className="day-number">{day}</span>
                      {dayAssignments.length > 0 && (
                        <div className="day-events">
                          {dayAssignments.slice(0, 3).map((a) => (
                            <div
                              key={a.id}
                              className="event-dot assignment"
                              title={a.title}
                            />
                          ))}
                          {dayAssignments.length > 3 && (
                            <div className="more-events">+{dayAssignments.length - 3}</div>
                          )}
                        </div>
                      )}
                      {dayAssignments.length > 0 && (
                        <div className="calendar-day-badge" aria-label={`${dayAssignments.length} assignments`}>
                          {dayAssignments.length}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Calendar days - Month View */}
            {calendarView === 'month' && (
              <div className="calendar-days">
                {days.map((day, index) => {
                  const dateObj = day ? new Date(currentDate.getFullYear(), currentDate.getMonth(), day) : null;
                  const key = dateObj ? toISODate(dateObj) : null;
                  const dayAssignments = key ? (assignmentsByDate.get(key) || []) : [];
                  const isVacation = key ? isVacationISO(key) : false;
                  return (
                    <div
                      key={index}
                      className={`calendar-day ${!day ? 'empty' : ''
                        } ${isToday(day) ? 'today' : ''
                        } ${isSelected(day) ? 'selected' : ''
                        } ${isVacation ? 'vacation' : ''}`}
                      onClick={() => handleDateClick(day)}
                      title={isVacation ? (getVacationReasonForISO(calendarVacations, key) || 'Vacation / day off') : undefined}
                    >
                      {day && (
                        <>
                          <span className="day-number">{day}</span>
                          {dayAssignments.length > 0 && (
                            <div className="day-events">
                              {dayAssignments.slice(0, 3).map((a) => (
                                <div
                                  key={a.id}
                                  className="event-dot assignment"
                                  title={a.title}
                                />
                              ))}
                              {dayAssignments.length > 3 && (
                                <div className="more-events">+{dayAssignments.length - 3}</div>
                              )}
                            </div>
                          )}
                          {dayAssignments.length > 0 && (
                            <div className="calendar-day-badge" aria-label={`${dayAssignments.length} assignments`}>
                              {dayAssignments.length}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Selected Date Assignments */}
        {selectedDate && (
          <div className="selected-date-events" style={{ marginTop: '1rem' }}>
            <h3 className="selected-date-title">
              Assignments for {selectedDate.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </h3>
            {selectedDateAssignments.length > 0 ? (
              <div className="events-list">
                {selectedDateAssignments.map((a) => {
                  const grade = gradeById.get(a.grade_id);
                  const section = a.section_id ? gradeSections.find(s => s.id === a.section_id) : null;
                  const gradeName = section ? `${grade?.name || 'Grade'} ${section.name}` : (grade?.name || 'Grade');
                  const attachmentUrl = a.attachment_path ? getAttachmentPublicUrl(a.attachment_path, 'assignments') : null;
                  const teacherName = getTeacherName(a);

                  return (
                    <div key={a.id} className="assignment-card">
                      <div className="assignment-card-head">
                        <div className="assignment-title">{a.title}</div>
                        <div className="assignment-meta">
                          <span className="assignment-chip">Assignment</span>
                          <span className="assignment-chip subtle">{gradeName}</span>
                        </div>
                      </div>

                      {a.description && <div className="assignment-description">{a.description}</div>}
                      <div className="assignment-byline">
                        Added by <strong>{teacherName}</strong>
                      </div>

                      <div className="assignment-actions">
                        {a.link_url && (
                          <a
                            className="assignment-link"
                            href={toAbsoluteUrl(a.link_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => openExternalUrl(a.link_url, e)}
                          >
                            Open link
                          </a>
                        )}
                        {attachmentUrl && (
                          <a
                            className="assignment-link secondary"
                            href={attachmentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => openExternalUrl(attachmentUrl, e)}
                          >
                            Download {a.attachment_name || 'attachment'}
                          </a>
                        )}
                      </div>

                      {/* Admin Actions */}
                      <div className="assignment-admin-actions">
                        <button
                          type="button"
                          className="assignment-admin-btn"
                          onClick={() => openEdit(a)}
                          disabled={readOnly}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="assignment-admin-btn danger"
                          onClick={() => {
                            if (readOnly) return;
                            handleDeleteAssignment(a);
                          }}
                          disabled={readOnly}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-events">
                <p>No assignments scheduled for this day</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Assignments List Section */}
      <div className="mds-school-cal__section">
        <div className="mds-school-cal__header">
          <div>
            <h3 className="mds-school-cal__title">Calendar</h3>
            <p className="mds-school-cal__sub">
              View all teacher assignments for this school, and manage vacation / days off.
            </p>
          </div>
          {readOnly && (
            <span className="mds-school-cal__pill" title="Subscription inactive — read-only mode">
              Read-only
            </span>
          )}
        </div>

        <div className="mds-school-cal__toolbar">
          <div className="mds-school-cal__field">
            <label htmlFor="ds-cal-start">From</label>
            <input
              id="ds-cal-start"
              type="date"
              value={rangeStart}
              onChange={(e) => setRangeStart(e.target.value)}
            />
          </div>
          <div className="mds-school-cal__field">
            <label htmlFor="ds-cal-end">To</label>
            <input
              id="ds-cal-end"
              type="date"
              value={rangeEnd}
              onChange={(e) => setRangeEnd(e.target.value)}
            />
          </div>
          <div className="mds-school-cal__field">
            <label htmlFor="ds-cal-grade">Grade</label>
            <select id="ds-cal-grade" value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)}>
              <option value="all">All grades</option>
              {(grades || []).map(g => {
                const sections = sectionsByGradeId.get(g.id) || [];
                return (
                  <React.Fragment key={g.id}>
                    <option value={g.id}>{g.name}</option>
                    {sections.map(s => (
                      <option key={`${g.id}:${s.id}`} value={`${g.id}:${s.id}`}>
                        {`↳ ${g.name} ${s.name}`}
                      </option>
                    ))}
                  </React.Fragment>
                );
              })}
            </select>
          </div>
          <div className="mds-school-cal__field">
            <label htmlFor="ds-cal-teacher">Teacher</label>
            <select id="ds-cal-teacher" value={teacherFilter} onChange={(e) => setTeacherFilter(e.target.value)}>
              <option value="all">All teachers</option>
              {(teachers || []).map(t => (
                <option key={t.id} value={t.id}>
                  {t.full_name || `${t.first_name || ''} ${t.last_name || ''}`.trim() || t.email || t.user_id || 'Teacher'}
                </option>
              ))}
            </select>
          </div>
          <div className="mds-school-cal__actions">
            <button type="button" className="mds-school-cal__btn" onClick={loadAll} disabled={!schoolId || loading}>
              Refresh
            </button>
          </div>
        </div>

        {error && <div className="mds-school-cal__muted" style={{ color: '#dc3545', fontWeight: 800 }}>{error}</div>}
        {loading && <div className="mds-school-cal__muted">Loading…</div>}

        {!loading && (
          <table className="mds-school-cal__table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Assignment</th>
                <th>Grade</th>
                <th>Teacher</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assignments.length === 0 ? (
                <tr>
                  <td colSpan="5" className="mds-school-cal__muted">
                    No assignments found in this date range.
                  </td>
                </tr>
              ) : (
                assignments.map((a) => {
                  const teacher = teacherById.get(a.created_by);
                  const teacherName = teacher
                    ? (teacher.full_name || `${teacher.first_name || ''} ${teacher.last_name || ''}`.trim() || teacher.email || 'Teacher')
                    : getTeacherName(a);
                  const attachmentUrl = a.attachment_path ? getAttachmentPublicUrl(a.attachment_path, 'assignments') : null;

                  return (
                    <tr key={a.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{a.assigned_date}</td>
                      <td>
                        <div style={{ fontWeight: 900 }}>{a.title}</div>
                        {a.description && <div className="mds-school-cal__muted" style={{ marginTop: 4 }}>{a.description}</div>}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                          {a.link_url && (
                            <a
                              href={toAbsoluteUrl(a.link_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mds-school-cal__pill"
                              onClick={(e) => openExternalUrl(a.link_url, e)}
                            >
                              Link
                            </a>
                          )}
                          {attachmentUrl && (
                            <a
                              href={attachmentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mds-school-cal__pill"
                              onClick={(e) => openExternalUrl(attachmentUrl, e)}
                            >
                              Attachment
                            </a>
                          )}
                        </div>
                      </td>
                      <td>{formatGradeLabel(a)}</td>
                      <td>{teacherName}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          className="mds-school-cal__btn primary"
                          onClick={() => openEdit(a)}
                          disabled={readOnly}
                        >
                          Edit
                        </button>{' '}
                        <button
                          type="button"
                          className="mds-school-cal__btn danger"
                          onClick={() => handleDeleteAssignment(a)}
                          disabled={readOnly}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="mds-school-cal__section">
        <div className="mds-school-cal__header">
          <div>
            <h3 className="mds-school-cal__title">Vacation / Days Off</h3>
            <p className="mds-school-cal__sub">
              These date ranges will appear grayed out on the main Calendar page for all users in this school.
            </p>
          </div>
        </div>

        <div className="mds-school-cal__toolbar">
          <div className="mds-school-cal__field" style={{ minWidth: 240 }}>
            <label htmlFor="ds-vac-title">Title (optional)</label>
            <input
              id="ds-vac-title"
              type="text"
              value={vacForm.title}
              onChange={(e) => setVacForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g. Winter break"
              disabled={readOnly}
            />
          </div>
          <div className="mds-school-cal__field">
            <label htmlFor="ds-vac-start">Start</label>
            <input
              id="ds-vac-start"
              type="date"
              value={vacForm.start_date}
              onChange={(e) => setVacForm(prev => ({ ...prev, start_date: e.target.value }))}
              disabled={readOnly}
            />
          </div>
          <div className="mds-school-cal__field">
            <label htmlFor="ds-vac-end">End</label>
            <input
              id="ds-vac-end"
              type="date"
              value={vacForm.end_date}
              onChange={(e) => setVacForm(prev => ({ ...prev, end_date: e.target.value }))}
              disabled={readOnly}
            />
          </div>
          <div className="mds-school-cal__actions">
            <button
              type="button"
              className="mds-school-cal__btn primary"
              onClick={addVacation}
              disabled={readOnly || vacSaving || !schoolId}
              title={readOnly ? 'Read-only mode' : 'Add vacation'}
            >
              Add
            </button>
          </div>
        </div>

        <table className="mds-school-cal__table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Start</th>
              <th>End</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {vacations.length === 0 ? (
              <tr>
                <td colSpan="4" className="mds-school-cal__muted">
                  No vacations in the current date range.
                </td>
              </tr>
            ) : (
              vacations.map(v => (
                <tr key={v.id}>
                  <td>{v.title || <span className="mds-school-cal__muted">—</span>}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{v.start_date}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{v.end_date}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      className="mds-school-cal__btn primary"
                      onClick={() => openVacationEdit(v)}
                      disabled={readOnly}
                    >
                      Edit
                    </button>{' '}
                    <button
                      type="button"
                      className="mds-school-cal__btn danger"
                      onClick={() => removeVacation(v)}
                      disabled={readOnly}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showVacEditModal && (
        <div className="mds-school-cal__modalOverlay" onClick={closeVacationEdit}>
          <div className="mds-school-cal__modal" onClick={(e) => e.stopPropagation()}>
            <div className="mds-school-cal__modalHead">
              <h3>Edit Vacation / Days Off</h3>
              <button className="mds-school-cal__close" onClick={closeVacationEdit} aria-label="Close">×</button>
            </div>
            <div className="mds-school-cal__modalBody">
              <div className="mds-school-cal__field">
                <label htmlFor="ds-vac-edit-title">Title</label>
                <input
                  id="ds-vac-edit-title"
                  type="text"
                  value={vacEditForm.title}
                  onChange={(e) => setVacEditForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. Winter break"
                  disabled={readOnly}
                />
              </div>

              <div className="mds-school-cal__grid2">
                <div className="mds-school-cal__field">
                  <label htmlFor="ds-vac-edit-start">Start</label>
                  <input
                    id="ds-vac-edit-start"
                    type="date"
                    value={vacEditForm.start_date}
                    onChange={(e) => setVacEditForm(prev => ({ ...prev, start_date: e.target.value }))}
                    disabled={readOnly}
                  />
                </div>
                <div className="mds-school-cal__field">
                  <label htmlFor="ds-vac-edit-end">End</label>
                  <input
                    id="ds-vac-edit-end"
                    type="date"
                    value={vacEditForm.end_date}
                    onChange={(e) => setVacEditForm(prev => ({ ...prev, end_date: e.target.value }))}
                    disabled={readOnly}
                  />
                </div>
              </div>

              <div className="mds-school-cal__actions" style={{ justifyContent: 'flex-end' }}>
                <button type="button" className="mds-school-cal__btn" onClick={closeVacationEdit} disabled={vacEditSaving}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="mds-school-cal__btn primary"
                  onClick={saveVacationEdit}
                  disabled={readOnly || vacEditSaving || !editingVacation}
                >
                  {vacEditSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="mds-school-cal__modalOverlay" onClick={closeEdit}>
          <div className="mds-school-cal__modal" onClick={(e) => e.stopPropagation()}>
            <div className="mds-school-cal__modalHead">
              <h3>Edit Assignment</h3>
              <button className="mds-school-cal__close" onClick={closeEdit} aria-label="Close">×</button>
            </div>
            <div className="mds-school-cal__modalBody">
              <div className="mds-school-cal__grid2">
                <div className="mds-school-cal__field">
                  <label htmlFor="ds-edit-date">Date</label>
                  <input
                    id="ds-edit-date"
                    type="date"
                    value={form.assigned_date}
                    onChange={(e) => setForm(prev => ({ ...prev, assigned_date: e.target.value }))}
                    disabled={readOnly}
                  />
                </div>
                <div className="mds-school-cal__field">
                  <label htmlFor="ds-edit-grade">Grade</label>
                  <select
                    id="ds-edit-grade"
                    value={form.grade_id}
                    onChange={(e) => setForm(prev => ({ ...prev, grade_id: e.target.value, section_id: '' }))}
                    disabled={readOnly}
                  >
                    <option value="">Select grade…</option>
                    {(grades || []).map(g => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mds-school-cal__grid2">
                <div className="mds-school-cal__field">
                  <label htmlFor="ds-edit-section">Section (optional)</label>
                  <select
                    id="ds-edit-section"
                    value={form.section_id || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, section_id: e.target.value }))}
                    disabled={readOnly || !form.grade_id}
                  >
                    <option value="">None</option>
                    {(sectionsByGradeId.get(form.grade_id) || []).map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="mds-school-cal__field">
                  <label htmlFor="ds-edit-link">Link (optional)</label>
                  <input
                    id="ds-edit-link"
                    type="url"
                    value={form.link_url}
                    onChange={(e) => setForm(prev => ({ ...prev, link_url: e.target.value }))}
                    placeholder="https://…"
                    disabled={readOnly}
                  />
                </div>
              </div>

              <div className="mds-school-cal__field">
                <label htmlFor="ds-edit-title">Title</label>
                <input
                  id="ds-edit-title"
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                  disabled={readOnly}
                />
              </div>

              <div className="mds-school-cal__field">
                <label htmlFor="ds-edit-desc">Description (optional)</label>
                <textarea
                  id="ds-edit-desc"
                  rows="4"
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  disabled={readOnly}
                />
              </div>

              <div className="mds-school-cal__field">
                <label>Attachment (optional)</label>
                {editing?.attachment_path && !removeExistingAttachment && (
                  <div className="mds-school-cal__muted" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    <span>Current: <strong>{editing.attachment_name || 'attachment'}</strong></span>
                    <button
                      type="button"
                      className="mds-school-cal__btn"
                      onClick={() => setRemoveExistingAttachment(true)}
                      disabled={readOnly}
                    >
                      Remove
                    </button>
                  </div>
                )}
                <input
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setAttachmentFile(file);
                      setRemoveExistingAttachment(true);
                    }
                  }}
                  disabled={readOnly}
                />
                {attachmentFile && (
                  <div className="mds-school-cal__muted">
                    Selected: <strong>{attachmentFile.name}</strong>
                  </div>
                )}
              </div>

              <div className="mds-school-cal__actions" style={{ justifyContent: 'flex-end' }}>
                <button type="button" className="mds-school-cal__btn" onClick={closeEdit} disabled={saving}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="mds-school-cal__btn primary"
                  onClick={saveEdit}
                  disabled={readOnly || saving}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Assignment Confirmation Dialog */}
      {showDeleteAssignmentConfirm && (
        <div className="mds-cal-confirm-overlay" onClick={cancelDeleteAssignment}>
          <div className="mds-cal-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="mds-cal-confirm-header">
              <div className="mds-cal-confirm-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <h2 className="mds-cal-confirm-title">Delete Assignment</h2>
            </div>
            <div className="mds-cal-confirm-body">
              <p className="mds-cal-confirm-message">
                Are you sure you want to delete this assignment? This action cannot be undone.
              </p>
              {assignmentToDelete && (
                <div className="mds-cal-confirm-details">
                  <strong>{assignmentToDelete.title}</strong>
                  {assignmentToDelete.assigned_date && (
                    <span className="mds-cal-confirm-muted"> • {assignmentToDelete.assigned_date}</span>
                  )}
                </div>
              )}
            </div>
            <div className="mds-cal-confirm-actions">
              <button className="mds-cal-confirm-cancel-btn" onClick={cancelDeleteAssignment}>
                Cancel
              </button>
              <button className="mds-cal-confirm-delete-btn" onClick={confirmDeleteAssignment}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Vacation Confirmation Dialog */}
      {showDeleteVacationConfirm && (
        <div className="mds-cal-confirm-overlay" onClick={cancelDeleteVacation}>
          <div className="mds-cal-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="mds-cal-confirm-header">
              <div className="mds-cal-confirm-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <h2 className="mds-cal-confirm-title">Remove Vacation</h2>
            </div>
            <div className="mds-cal-confirm-body">
              <p className="mds-cal-confirm-message">
                Are you sure you want to remove this vacation range? This action cannot be undone.
              </p>
              {vacationToDelete && (
                <div className="mds-cal-confirm-details">
                  <strong>{vacationToDelete.title || 'Untitled Vacation'}</strong>
                  {vacationToDelete.start_date && vacationToDelete.end_date && (
                    <span className="mds-cal-confirm-muted">
                      {' '}• {vacationToDelete.start_date} to {vacationToDelete.end_date}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="mds-cal-confirm-actions">
              <button className="mds-cal-confirm-cancel-btn" onClick={cancelDeleteVacation}>
                Cancel
              </button>
              <button className="mds-cal-confirm-delete-btn" onClick={confirmDeleteVacation}>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardSchoolCalendar;


